import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sunrise, Sun, Sunset, Moon, Heart, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { timeInZone, partOfDay } from "@/lib/timezones";
import { Button } from "@/components/ui/button";

const ICONS = { sunrise: Sunrise, sun: Sun, sunset: Sunset, moon: Moon };
const NUDGES = [
  { emoji: "❤️", label: "Love you", msg: "sent you love" },
  { emoji: "🤗", label: "Hug", msg: "sent a virtual hug" },
  { emoji: "😘", label: "Kiss", msg: "blew you a kiss" },
  { emoji: "🥺", label: "Miss you", msg: "misses you" },
  { emoji: "☕", label: "Thinking", msg: "is thinking of you" },
];

function ClockCard({ person, highlight }) {
  const t = person?.timezone ? timeInZone(person.timezone) : null;
  const pod = t ? partOfDay(t.hour) : null;
  const Icon = pod ? ICONS[pod.icon] : Sun;
  if (!person?.name) {
    return (
      <div className="flex-1 rounded-2xl border border-dashed border-border/80 p-4 text-center text-xs text-muted-foreground">
        Waiting for partner to join…
      </div>
    );
  }
  return (
    <div className={`flex-1 rounded-2xl p-4 border ${highlight ? "bg-gradient-to-br from-accent/70 to-secondary/40 border-primary/30" : "bg-secondary/60 border-border/60"}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-foreground truncate">{person.name}</span>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-1 font-mono text-2xl font-bold tracking-tight">{t.time}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{person.city} · {t.day}</div>
    </div>
  );
}

function Countdown({ reunion }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const i = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  if (!reunion) return null;
  const target = new Date(reunion + "T00:00:00").getTime();
  const diff = target - now;
  const past = diff <= 0;
  const abs = Math.abs(diff);
  const days = Math.floor(abs / 86400000);
  const hours = Math.floor((abs % 86400000) / 3600000);
  const mins = Math.floor((abs % 3600000) / 60000);
  const secs = Math.floor((abs % 60000) / 1000);
  const cells = [[days, "days"], [hours, "hrs"], [mins, "min"], [secs, "sec"]];

  return (
    <div className="relative overflow-hidden rounded-3xl bg-dusk-indigo text-white p-6 shadow-lg" data-testid="countdown-hero">
      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/40 blur-2xl" />
      <div className="absolute -left-6 -bottom-6 h-24 w-24 rounded-full bg-sunset-gold/30 blur-2xl" />
      <div className="relative">
        <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white/70">
          {past ? "Since we were together" : "Until we're together"}
        </div>
        <div className="mt-4 grid grid-cols-4 gap-2">
          {cells.map(([v, l]) => (
            <div key={l} className="text-center">
              <div data-testid={`countdown-${l}`} className={`font-mono text-2xl sm:text-3xl font-bold ${l === "days" ? "text-white" : "text-white/90"}`}>
                {String(v).padStart(2, "0")}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-white/60 mt-0.5">{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Home({ couple, slot, me, partner, setTab }) {
  const [hearts, setHearts] = useState([]);
  const [dq, setDq] = useState(null);

  useEffect(() => {
    api.get(`/couples/${couple.id}/daily-question`).then((r) => setDq(r.data)).catch(() => {});
  }, [couple.id]);

  const daysTogether = useMemo(() => {
    if (!couple.together_since) return null;
    return Math.max(0, Math.floor((Date.now() - new Date(couple.together_since + "T00:00:00").getTime()) / 86400000));
  }, [couple.together_since]);

  const sendNudge = async (n) => {
    const id = Date.now() + Math.random();
    setHearts((h) => [...h, { id, emoji: n.emoji }]);
    setTimeout(() => setHearts((h) => h.filter((x) => x.id !== id)), 2200);
    try {
      await api.post(`/couples/${couple.id}/nudge`, { from_slot: slot, emoji: n.emoji, message: n.msg });
      toast.success(`${n.emoji} ${partner?.name ? `Sent to ${partner.name}!` : "Sent!"}`);
    } catch {
      toast.error("Could not send right now.");
    }
  };

  return (
    <div className="space-y-5">
      {/* greeting */}
      <div>
        <p className="text-sm text-muted-foreground">Hi {me?.name} 👋</p>
        <h1 className="font-serif text-3xl font-semibold leading-tight">
          {partner?.name ? <>You &amp; {partner.name}</> : "Your space is ready"}
        </h1>
      </div>

      <Countdown reunion={couple.reunion_date} />

      {/* clocks */}
      <div>
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Our time right now</div>
        <div className="flex gap-3">
          <ClockCard person={me} highlight />
          <ClockCard person={partner} />
        </div>
      </div>

      {/* stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-card border border-border/70 p-4">
          <div className="text-2xl font-bold text-primary" data-testid="days-together">{daysTogether ?? "—"}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">days together</div>
        </div>
        <div className="rounded-2xl bg-card border border-border/70 p-4">
          <div className="text-2xl font-bold text-primary">{dq?.streak ?? 0}🔥</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">question streak</div>
        </div>
      </div>

      {/* daily question preview */}
      {dq && (
        <button
          data-testid="home-daily-card" onClick={() => setTab("daily")}
          className="w-full text-left rounded-3xl bg-gradient-to-br from-accent/60 to-card border border-border/70 p-5 active:scale-[0.99] transition-transform"
        >
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-primary">Today's question</div>
          <p className="font-serif text-xl font-medium mt-2 leading-snug">{dq.question}</p>
          <div className="mt-3 text-xs text-muted-foreground">
            {dq.revealed ? "Both answered — tap to read 💌" : dq[`${slot}_answered`] ? "Waiting for your partner…" : "Tap to answer →"}
          </div>
        </button>
      )}

      {/* nudge bar */}
      <div className="relative rounded-3xl bg-card border border-border/70 p-5">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="h-4 w-4 text-primary" />
          <span className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">Send a little love</span>
        </div>
        <div className="grid grid-cols-5 gap-2">
          {NUDGES.map((n) => (
            <button
              key={n.label} data-testid={`nudge-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => sendNudge(n)}
              className="flex flex-col items-center gap-1 rounded-2xl bg-secondary/60 hover:bg-accent border border-border/50 py-3 active:scale-90 transition-all"
            >
              <span className="text-xl">{n.emoji}</span>
              <span className="text-[9px] font-medium text-muted-foreground">{n.label}</span>
            </button>
          ))}
        </div>
        {/* floating hearts */}
        <div className="pointer-events-none absolute inset-x-0 bottom-8 flex justify-center">
          {hearts.map((h) => (
            <span key={h.id} className="absolute text-3xl float-heart">{h.emoji}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
